// Number lifecycle on Number Ownership: Add numbers, Allot (any org, with push to switch),
// and the per-number "Number details" drawer (holder history, live switch rows, billing
// claim, push history, actions, Settings). The decisions are the server's
// (NumberLifecycleController); the shaping lives in numberLifecycleRules.ts.
//
// Every switch write is preview → confirm → apply with the preview's hash. Nothing here
// writes a label billing does not claim: a BLOCKED "would be unbilled" plan offers the one
// fix, "Add <label> to <org>'s billing patterns", which is its own audited action.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Archive, ArrowRightLeft, Check, Info, Loader2, Plus, RefreshCw, Send, Settings2, Undo2, X,
  Workflow, Receipt, Server,
} from 'lucide-react';
import type { Provider, ProviderNumber } from 'api/services/providers.service';
import type { Organization } from 'api/services/organizations.service';
import type { LockInTerm } from 'api/services/numberOwnership.service';
import type { SwitchPushApplyResult, SwitchPushPlan, SwitchPushPreview } from 'api/services/switchPush.service';
import {
  numberLifecycleService, AddResult, AllotPreview, AllotResult, FlowInfo, NumberDetails, NumberSettings,
  SettingsPreview, ReleaseResult,
} from 'api/services/numberLifecycle.service';
import {
  parseNumberList, flowKey, flowOption, pickableFlows, unbilledLabel, pushOutcomeText, outcomePill,
  allotBlockReason, webhookSummary, draftFrom, changeFrom, isEmptyChange, SettingsDraft,
} from './numberLifecycleRules';
import { ACTION_TEXT, actionPill, describeRow, PARKED_NOTE, canRollback } from './switchPushRules';
import { ivrPublishService, type AttachCheck } from 'api/services/ivrPublish.service';
import { formatWhen, shortId } from './numberOwnershipRules';

// ── shared bits ──────────────────────────────────────────────────────────────

const Modal: React.FC<{ onClose: () => void; busy: boolean; wide?: boolean; children: React.ReactNode }> = ({
  onClose, busy, wide, children,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
       style={{ background: 'rgba(0,0,0,.45)' }} onClick={() => !busy && onClose()}>
    <div className={`tl-card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}
         onClick={e => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>{children}</label>
);

const Note: React.FC<{ tone?: 'muted' | 'amber' | 'danger' | 'ok'; children: React.ReactNode }> = ({ tone = 'muted', children }) => {
  const style = tone === 'danger' ? { background: 'var(--danger-bg)', color: 'var(--danger-text)' }
    : tone === 'amber' ? { background: 'var(--amber-tint)', color: 'var(--amber-label)' }
      : tone === 'ok' ? { background: 'var(--success-bg, var(--surface-raised))', color: 'var(--success-text, var(--text-muted))' }
        : { background: 'var(--surface-raised)', color: 'var(--text-muted)' };
  const Icon = tone === 'danger' || tone === 'amber' ? AlertTriangle : tone === 'ok' ? Check : Info;
  return (
    <div className="text-[12.5px] px-3 py-2 rounded-lg flex items-start gap-1.5" style={style}>
      <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> <div className="flex-1">{children}</div>
    </div>
  );
};

/** One switch plan, readable: action, current row → new row, flow, recording folder, warnings. */
const PlanCard: React.FC<{ plan: SwitchPushPlan; onAddPattern?: (label: string) => void; busy?: boolean }> = ({
  plan, onAddPattern, busy,
}) => {
  const label = unbilledLabel(plan);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap text-[12.5px]">
        <span className={actionPill(plan.action)}>{ACTION_TEXT[plan.action]}</span>
        <span style={{ color: 'var(--text-muted)' }}>on {plan.switchName || 'the home switch'}</span>
      </div>
      <div className="text-[12.5px] text-gray-900 dark:text-white">{plan.reason}</div>
      {plan.target && (
        <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
          Now: {plan.target.cliNumber} · {describeRow(plan.target)} · operator {plan.target.operatorId || '∅'}
        </div>
      )}
      {plan.create && (
        <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
          INSERT {plan.create.cliNumber} ({plan.create.spellingRule}) · {plan.create.dialingCode} ·{' '}
          {plan.create.transcribeName} · {plan.create.defaultLanguage} — {plan.create.defaultsSource}
        </div>
      )}
      {plan.flow && (
        <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
          Flow {flowKey(plan.flow)} · recordings file under{' '}
          <strong>{plan.flow.recordingFolders.length ? plan.flow.recordingFolders.join(', ') : '(unknown)'}</strong>
          {' '}(the flow&rsquo;s business_name, not the number&rsquo;s label)
        </div>
      )}
      {plan.warnings.map(w => <Note key={w} tone="amber">{w}</Note>)}
      {(plan.residue || []).some(r => r.rows.length > 0) && (
        <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
          Residue (never written):{' '}
          {plan.residue.filter(r => r.rows.length > 0).map(r => `${r.switchName}: ${r.rows.map(describeRow).join(', ')}`).join(' · ')}
        </div>
      )}
      {label && onAddPattern && (
        <Note tone="danger">
          Label <strong>{label}</strong> is not billed to {plan.holderName || 'the holder'} on this switch&rsquo;s kind, so it will
          not be written.
          <div className="mt-1.5">
            <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={busy} onClick={() => onAddPattern(label)}>
              <Receipt className="w-3.5 h-3.5" /> Add {label} to {plan.holderName || 'the holder'}&rsquo;s billing patterns
            </button>
          </div>
        </Note>
      )}
    </div>
  );
};

/** The flow picker: flows with states on the number's home switch, with folder and DIDs. */
const FlowPicker: React.FC<{
  providerId?: number; value: string; onChange: (key: string) => void; allowKeep?: boolean;
}> = ({ providerId, value, onChange, allowKeep = true }) => {
  const [flows, setFlows] = useState<FlowInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    let live = true;
    if (!providerId) return () => { live = false; };
    numberLifecycleService.flows(providerId).then(r => {
      if (!live) return;
      if (r.success) setFlows(r.data || []); else setError(r.error || 'Could not read the flows');
    });
    return () => { live = false; };
  }, [providerId]);
  const options = useMemo(() => pickableFlows(flows || [], q), [flows, q]);
  const chosen = (flows || []).find(f => `${f.operatorId}\u0000${f.serviceId}` === value);
  // Phase 4: the IVR app's state in cpass (versions on this switch, unpublished drafts).
  const [app, setApp] = useState<AttachCheck | null>(null);
  useEffect(() => {
    let live = true;
    setApp(null);
    if (!chosen || !providerId) return () => { live = false; };
    ivrPublishService.attachCheck(chosen.operatorId, chosen.serviceId, providerId).then(r => {
      if (live && r.success && r.data) setApp(r.data);
    });
    return () => { live = false; };
  }, [chosen?.operatorId, chosen?.serviceId, providerId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <Label>IVR flow (state_details on the home switch)</Label>
      {error && <Note tone="danger">{error}</Note>}
      <input className="tl-input mb-1.5" placeholder="Search flows, recording folders or DIDs" value={q}
             onChange={e => setQ(e.target.value)} />
      <select className="tl-input" value={value} onChange={e => onChange(e.target.value)} disabled={flows === null}>
        {allowKeep && <option value="">Keep the current flow (create needs a flow)</option>}
        {options.map(f => (
          <option key={`${f.operatorId}/${f.serviceId}`} value={`${f.operatorId}\u0000${f.serviceId}`}>{flowOption(f)}</option>
        ))}
      </select>
      {flows === null && !error && <div className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>Reading the switch…</div>}
      {chosen && (
        <div className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
          Recordings for calls on this flow go to <strong>{chosen.recordingFolders.join(', ') || '(unknown)'}</strong>.
          {chosen.dids.length > 0 && <> Also used by {chosen.dids.slice(0, 6).map(d => `${d.cliNumber} (${d.businessId || '∅'})`).join(', ')}
            {chosen.dids.length > 6 ? ` and ${chosen.dids.length - 6} more` : ''}.</>}
          {chosen.languages.length > 0 && <> Languages: {chosen.languages.join(', ')}.</>}
        </div>
      )}
      {chosen && app && (
        <div className="text-[12px] mt-1" style={{ color: app.ready && !app.openDrafts.length ? 'var(--text-muted)' : '#b45309' }}>
          IVR app in cpass: {app.presentOnSwitch ? `versions on this switch in ${app.languagesOnSwitch.join(', ')}` : 'not imported for this switch'}
          {app.openDrafts.length > 0 && <> · unpublished draft(s) in {app.openDrafts.join(', ')} — callers will hear the published version</>}
          {app.notes.map((n, i) => <div key={i}>{n}</div>)}
          <a className="text-blue-700 ml-1" href={`/telephony/ivr/app?op=${encodeURIComponent(chosen.operatorId)}&svc=${encodeURIComponent(chosen.serviceId)}`}>
            Open the app
          </a>
        </div>
      )}
    </div>
  );
};

const splitFlow = (v: string): { operatorId: string | null; serviceId: string | null } => {
  if (!v) return { operatorId: null, serviceId: null };
  const [operatorId, serviceId] = v.split('\u0000');
  return { operatorId, serviceId };
};

// ── Add numbers ───────────────────────────────────────────────────────────────

export const AddNumbersModal: React.FC<{ providers: Provider[]; onClose: (changed: boolean) => void }> = ({ providers, onClose }) => {
  const [providerId, setProviderId] = useState<number | ''>('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AddResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = parseNumberList(text);

  const run = async (dryRun: boolean) => {
    if (!providerId) return;
    setBusy(true); setError(null);
    const r = await numberLifecycleService.add(providerId, list, dryRun);
    setBusy(false);
    if (r.success && r.data) setResult(r.data); else setError(r.error || 'Refused');
  };

  return (
    <Modal onClose={() => onClose(!!result && !result.dryRun)} busy={busy}>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Add numbers</h2>
      <p className="text-[12.5px] mb-3" style={{ color: 'var(--text-muted)' }}>
        New numbers join the trunk&rsquo;s pool (AVAILABLE, no holder). On a trunk that terminates on our switch, cpass also
        gets a parked routing row (FREE, inactive). Nothing is written to the switch until the number is allotted and pushed.
        A number already known in any spelling (+91…, 0091…, 0…) is refused.
      </p>
      <div className="space-y-3">
        <div>
          <Label>Trunk</Label>
          <select className="tl-input" value={providerId} onChange={e => { setProviderId(e.target.value ? Number(e.target.value) : ''); setResult(null); }}>
            <option value="">Select a trunk…</option>
            {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <Label>Numbers (one per line)</Label>
          <textarea className="tl-input font-mono text-[12.5px]" rows={6} value={text}
                    onChange={e => { setText(e.target.value); setResult(null); }}
                    placeholder={'+912265197304\n02265197305'} />
          <div className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>{list.length} number{list.length === 1 ? '' : 's'}</div>
        </div>
        {error && <Note tone="danger">{error}</Note>}
        {result && (
          <div className="space-y-1">
            <div className="text-[12.5px] font-medium text-gray-900 dark:text-white">
              {result.dryRun ? 'Would add' : 'Added'} {result.created.length} · refused {result.refused.length}
            </div>
            {result.created.map(c => (
              <div key={c.input} className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                <span className="tl-pill tl-pill-success mr-1">{result.dryRun ? 'ok' : 'added'}</span>{c.e164} · routing row {c.routingRow}
              </div>
            ))}
            {result.refused.map(c => (
              <div key={c.input} className="text-[12px]" style={{ color: 'var(--danger-text)' }}>
                <span className="tl-pill tl-pill-coral mr-1">refused</span>{c.input}: {c.reason}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button className="tl-btn tl-btn-secondary" onClick={() => onClose(!!result && !result.dryRun)} disabled={busy}>
          {result && !result.dryRun ? 'Done' : 'Cancel'}
        </button>
        {!(result && !result.dryRun) && (
          <>
            <button className="tl-btn tl-btn-secondary" disabled={busy || !providerId || list.length === 0} onClick={() => run(true)}>
              Check
            </button>
            <button className="tl-btn tl-btn-primary" disabled={busy || !providerId || !result || !result.dryRun || result.created.length === 0}
                    title={!result ? 'Check first' : undefined} onClick={() => run(false)}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add {result?.created.length || ''}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

// ── Allot / reassign ──────────────────────────────────────────────────────────

export const AllotModal: React.FC<{
  number: ProviderNumber; orgs: Organization[]; onClose: (changed: boolean) => void;
}> = ({ number, orgs, onClose }) => {
  const [organizationId, setOrganizationId] = useState('');
  const [term, setTerm] = useState<LockInTerm>('MONTHLY');
  const [autoRenew, setAutoRenew] = useState(true);
  const [force, setForce] = useState(false);
  const [push, setPush] = useState(true);
  const [flow, setFlow] = useState('');
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<AllotPreview | null>(null);
  const [result, setResult] = useState<AllotResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const req = () => ({
    organizationId, term, autoRenew, force, push, ...splitFlow(flow), note: note || null,
  });
  const reset = () => { setPreview(null); setInfo(null); };

  const doPreview = async () => {
    setBusy(true); setError(null);
    const r = await numberLifecycleService.allotPreview(number.id as number, req());
    setBusy(false);
    if (r.success && r.data) setPreview(r.data); else { setPreview(null); setError(r.error || 'Refused'); }
  };

  const doAllot = async () => {
    setBusy(true); setError(null);
    const r = await numberLifecycleService.allot(number.id as number, { ...req(), previewHash: preview?.previewHash || null });
    setBusy(false);
    if (r.success && r.data) setResult(r.data); else setError(r.error || 'Refused');
  };

  const addPattern = async (label: string) => {
    setBusy(true); setError(null);
    // The target does not hold it yet: name it as the would-be holder, and put the pattern on it.
    const r = await numberLifecycleService.addBillingPattern(number.id as number, organizationId, label, organizationId);
    setBusy(false);
    if (r.success && r.data) { setInfo(`${label} added to ${r.data.organization.name}'s ${r.data.sourceKind} billing patterns.`); doPreview(); }
    else setError(r.error || 'Refused');
  };

  const needsForce = !!preview?.ownership.inLockIn;
  const block = allotBlockReason({
    organizationId, previewed: !!preview, refusal: preview?.ownership.refusal, force, needsForce, push, mode: preview?.mode,
  });
  const plan = preview?.switch?.plans?.[0];
  const held = !!number.purchased_by_organization_id;

  return (
    <Modal onClose={() => onClose(!!result)} busy={busy} wide>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
        {held ? 'Reassign' : 'Allot'} {number.e164}
      </h2>
      {result ? (
        <div className="space-y-2 mt-2">
          <Note tone="ok">Now held by {orgs.find(o => o.id === organizationId)?.name || organizationId}. Tenure starts now. Routing row: {result.routingRow}.</Note>
          {result.push && (
            <div className="text-[12.5px]">
              <span className={outcomePill(result.push.outcome)}>{result.push.outcome}</span>{' '}
              <span style={{ color: 'var(--text-muted)' }}>{pushOutcomeText(result.push)}</span>
            </div>
          )}
          <div className="flex justify-end"><button className="tl-btn tl-btn-primary" onClick={() => onClose(true)}>Done</button></div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Organization (provider or business)</Label>
              <select className="tl-input" value={organizationId} onChange={e => { setOrganizationId(e.target.value); setForce(false); reset(); }}>
                <option value="">Select…</option>
                {orgs.map(o => (
                  <option key={o.id} value={o.id} disabled={!o.businessId && !(o as any).business_id}>
                    {o.name}{o.type === 'BUSINESS' ? ' (client)' : ' (provider)'}{!o.businessId && !(o as any).business_id ? ' — no business_id' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Term</Label>
                <select className="tl-input" value={term} onChange={e => { setTerm(e.target.value as LockInTerm); reset(); }}>
                  <option value="MONTHLY">Monthly</option>
                  <option value="ANNUAL">Annual</option>
                </select>
              </div>
              <div>
                <Label>At term end</Label>
                <select className="tl-input" value={autoRenew ? 'r' : 's'} onChange={e => { setAutoRenew(e.target.value === 'r'); reset(); }}>
                  <option value="r">Renew</option>
                  <option value="s">Expire</option>
                </select>
              </div>
            </div>
          </div>
          <label className="flex items-start gap-2 text-[12.5px] cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={push} onChange={e => { setPush(e.target.checked); reset(); }} />
            <span><strong>Push to switch</strong> in the same action (label = the org&rsquo;s business_id; creates the row if it is missing)</span>
          </label>
          {push && <FlowPicker providerId={number.provider_id} value={flow} onChange={v => { setFlow(v); reset(); }} />}
          <div>
            <Label>Note (on the new tenure)</Label>
            <input className="tl-input" value={note} onChange={e => setNote(e.target.value)} placeholder="optional" />
          </div>
          {error && <Note tone="danger">{error}</Note>}
          {info && <Note tone="ok">{info}</Note>}
          {preview && (
            <div className="space-y-2 rounded-lg p-3" style={{ background: 'var(--surface-raised)' }}>
              <div className="text-[12.5px] text-gray-900 dark:text-white">
                {preview.ownership.currentHolder
                  ? <>Closes {preview.ownership.currentHolder.name}&rsquo;s tenure; </>
                  : <>From the pool; </>}
                {preview.target.name} ({preview.target.businessId}) holds it from now until {formatWhen(preview.ownership.newTermEndsAt)}.
              </div>
              {needsForce && (
                <Note tone="amber">
                  {preview.ownership.refusal || 'Inside the current holder’s lock-in.'}
                  <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
                    <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} /> End their term early
                  </label>
                </Note>
              )}
              {push && preview.mode === 'OFF' && <Note>Switch push is off on the server: the switch will not be touched.</Note>}
              {push && preview.mode === 'PREVIEW' && <Note tone="amber">Preview mode: the plan below is recorded, NOT written.</Note>}
              {plan && <PlanCard plan={plan} onAddPattern={addPattern} busy={busy} />}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button className="tl-btn tl-btn-secondary" onClick={() => onClose(false)} disabled={busy}>Cancel</button>
            <button className="tl-btn tl-btn-secondary" onClick={doPreview} disabled={busy || !organizationId}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Preview
            </button>
            <button className={`tl-btn ${force ? 'tl-btn-danger' : 'tl-btn-primary'}`} onClick={doAllot}
                    disabled={busy || !!block} title={block || undefined}>
              <ArrowRightLeft className="w-4 h-4" /> {held ? 'Reassign' : 'Allot'}{push ? ' + push' : ''}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ── Push / change flow (one number) ───────────────────────────────────────────

const PushPanel: React.FC<{ details: NumberDetails; onDone: () => void }> = ({ details, onDone }) => {
  const n = details.number;
  const [flow, setFlow] = useState('');
  const [cli, setCli] = useState('');
  const [preview, setPreview] = useState<SwitchPushPreview | null>(null);
  const [result, setResult] = useState<SwitchPushApplyResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const req = () => ({ ...splitFlow(flow), cliNumber: cli.trim() || null });

  const doPreview = async () => {
    setBusy(true); setError(null); setResult(null);
    const r = await numberLifecycleService.pushPreview(n.id as number, req());
    setBusy(false);
    if (r.success && r.data) setPreview(r.data); else { setPreview(null); setError(r.error || 'Refused'); }
  };
  const doApply = async () => {
    if (!preview) return;
    setBusy(true); setError(null);
    const r = await numberLifecycleService.pushApply(n.id as number, { ...req(), previewHash: preview.previewHash });
    setBusy(false);
    if (r.success && r.data) { setResult(r.data); setPreview(null); onDone(); } else setError(r.error || 'Refused');
  };
  const addPattern = async (label: string) => {
    setBusy(true); setError(null);
    const r = await numberLifecycleService.addBillingPattern(n.id as number, null, label);
    setBusy(false);
    if (r.success) doPreview(); else setError(r.error || 'Refused');
  };
  const plan = preview?.plans?.[0];
  const canApply = preview && preview.mode === 'APPLY' && plan && (plan.action === 'ASSIGN' || plan.action === 'PARK' || plan.action === 'CREATE');

  return (
    <div className="space-y-2">
      <FlowPicker providerId={n.provider_id} value={flow} onChange={v => { setFlow(v); setPreview(null); }} />
      <div>
        <Label>cli_number spelling for a new row (optional; default follows the switch&rsquo;s neighbours)</Label>
        <input className="tl-input" value={cli} onChange={e => { setCli(e.target.value); setPreview(null); }} placeholder={n.e164} />
      </div>
      {error && <Note tone="danger">{error}</Note>}
      {plan && <PlanCard plan={plan} onAddPattern={addPattern} busy={busy} />}
      {preview && preview.mode !== 'APPLY' && <Note tone="amber">Switch push is {preview.mode.toLowerCase()}: apply is disabled on the server.</Note>}
      {result && result.results.map(x => (
        <Note key={x.pushId || x.e164} tone={x.status === 'OK' ? 'ok' : x.status === 'FAILED' ? 'danger' : 'muted'}>
          {ACTION_TEXT[x.action]} · {x.status}{x.pushId ? ` (push #${x.pushId})` : ''} — {x.detail}
        </Note>
      ))}
      <div className="flex gap-2 justify-end">
        <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={doPreview} disabled={busy}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Preview
        </button>
        <button className="tl-btn tl-btn-primary tl-btn-sm" onClick={doApply} disabled={busy || !canApply}>
          <Send className="w-3.5 h-3.5" /> Apply
        </button>
      </div>
    </div>
  );
};

// ── Release / retire (one number, with park) ─────────────────────────────────

const ReleasePanel: React.FC<{ details: NumberDetails; retire?: boolean; onDone: () => void }> = ({ details, retire, onDone }) => {
  const n = details.number;
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [park, setPark] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ReleaseResult | { switch?: unknown } | null>(null);
  const locked = !!n.in_lock_in;
  const run = async () => {
    setBusy(true); setError(null);
    const r = retire
      ? await numberLifecycleService.retire(n.id as number, reason.trim(), park)
      : await numberLifecycleService.release(n.id as number, reason.trim(), force, park);
    setBusy(false);
    if (r.success && r.data) { setDone(r.data as any); onDone(); } else setError(r.error || 'Refused');
  };
  if (done) {
    const p = (done as ReleaseResult).push;
    return <Note tone="ok">{retire ? 'Retired.' : 'Released to the pool.'} {p ? pushOutcomeText(p) : retire && park ? 'Park: see the push history.' : 'Switch not touched.'}</Note>;
  }
  return (
    <div className="space-y-2">
      <input className="tl-input" value={reason} onChange={e => setReason(e.target.value)}
             placeholder={retire ? 'Reason (required), e.g. dead line' : 'Reason, e.g. contract ended'} />
      {!retire && locked && (
        <label className="flex items-center gap-2 text-[12.5px]"><input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} />
          Inside lock-in until {formatWhen(n.lock_in_ends_at)} — end it early</label>
      )}
      <label className="flex items-center gap-2 text-[12.5px]"><input type="checkbox" checked={park} onChange={e => setPark(e.target.checked)} />
        Park on the switch as FREE (label FREE, Free flow)</label>
      <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{PARKED_NOTE}</div>
      {error && <Note tone="danger">{error}</Note>}
      <div className="flex justify-end">
        <button className="tl-btn tl-btn-danger tl-btn-sm" onClick={run}
                disabled={busy || (retire && !reason.trim()) || (!retire && locked && !force)}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : retire ? <Archive className="w-3.5 h-3.5" /> : <Undo2 className="w-3.5 h-3.5" />}
          {retire ? 'Retire' : 'Release'}{park ? ' + park' : ''}
        </button>
      </div>
    </div>
  );
};

// ── Settings tab ──────────────────────────────────────────────────────────────

const SettingsTab: React.FC<{ numberId: number }> = ({ numberId }) => {
  const [s, setS] = useState<NumberSettings | null>(null);
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [preview, setPreview] = useState<SettingsPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await numberLifecycleService.settings(numberId);
    if (r.success && r.data) { setS(r.data); setDraft(draftFrom(r.data)); setPreview(null); }
    else setError(r.error || 'Could not load the settings');
  }, [numberId]);
  useEffect(() => { load(); }, [load]);

  if (!s || !draft) return <div className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>{error || 'Loading…'}</div>;
  const change = changeFrom(s, draft);
  const set = (p: Partial<SettingsDraft>) => { setDraft({ ...draft, ...p }); setPreview(null); };

  const doPreview = async () => {
    setBusy(true); setError(null); setInfo(null);
    const r = await numberLifecycleService.settingsPreview(numberId, change);
    setBusy(false);
    if (r.success && r.data) setPreview(r.data); else setError(r.error || 'Refused');
  };
  const doApply = async () => {
    if (!preview) return;
    setBusy(true); setError(null);
    const r = await numberLifecycleService.settingsApply(numberId, change, preview.previewHash);
    setBusy(false);
    if (r.success && r.data) { setInfo(`${r.data.status}: ${r.data.detail} (push #${r.data.pushId}, roll back from the history)`); load(); }
    else setError(r.error || 'Refused');
  };
  const toggleOutbound = async () => {
    setBusy(true); setError(null);
    const r = await numberLifecycleService.setOutbound(numberId, !s.outboundAllowed);
    setBusy(false);
    if (r.success) load(); else setError(r.error || 'Refused');
  };
  const toggleLookup = async () => {
    setBusy(true); setError(null);
    const r = await numberLifecycleService.setCallerLookup(numberId, !s.callerLookup?.enabled);
    setBusy(false);
    if (r.success) load(); else setError(r.error || 'Refused');
  };

  return (
    <div className="space-y-3">
      {error && <Note tone="danger">{error}</Note>}
      {info && <Note tone="ok">{info}</Note>}

      <div className="rounded-lg p-3 space-y-2" style={{ background: 'var(--surface-raised)' }}>
        <div className="text-[12.5px] font-medium text-gray-900 dark:text-white">In cpass (applies at once, audited)</div>
        <div className="flex items-start justify-between gap-2">
          <div className="text-[12.5px]">
            <strong>Outgoing calls</strong>: {s.outboundAllowed ? 'allowed' : 'BARRED'}
            <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>Covers: {s.outboundCovers}</div>
            <div className="text-[12px]" style={{ color: 'var(--amber-label)' }}>Not covered: {s.outboundNotCovered}</div>
          </div>
          <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={busy} onClick={toggleOutbound}>
            {s.outboundAllowed ? 'Bar outgoing' : 'Allow outgoing'}
          </button>
        </div>
        <div className="flex items-start justify-between gap-2">
          <div className="text-[12.5px]">
            <strong>Caller lookup (call identity)</strong>: {s.callerLookup?.error ? 'unknown' : s.callerLookup?.enabled ? 'on' : 'off'}
            {' '}· {(s.callerLookup?.sources || []).length} source(s)
            {(s.callerLookup?.sources || []).map(x => (
              <div key={x.id} className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                {x.vendor} {x.vendorKey} · {shortId(x.organizationId)} · {x.enabled ? 'enabled' : 'disabled'}{x.hasWebhookToken ? ' · token set' : ''}
              </div>
            ))}
            <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{s.callerLookup?.note || s.callerLookup?.error}</div>
          </div>
          <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={busy || (s.callerLookup?.sources || []).length === 0}
                  title={(s.callerLookup?.sources || []).length === 0 ? 'No source for this DID; one is set up by hand with the vendor details' : undefined}
                  onClick={toggleLookup}>
            {s.callerLookup?.enabled ? 'Turn off' : 'Turn on'}
          </button>
        </div>
        <div className="text-[12.5px]">
          <strong>Do not pick incoming</strong>: <span className="tl-pill">unavailable</span>
          <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{s.incomingReject.reason}</div>
          {s.incomingReject.alternative && <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>Instead: {s.incomingReject.alternative}</div>}
        </div>
      </div>

      <div className="rounded-lg p-3 space-y-2" style={{ background: 'var(--surface-raised)' }}>
        <div className="text-[12.5px] font-medium text-gray-900 dark:text-white">
          On the switch ({s.switch || 'home switch'}{s.row ? ` · row ${s.row.id} ${s.row.cliNumber}` : ''}) — preview, apply, roll back
        </div>
        {s.switchError ? <Note tone="danger">{s.switchError}</Note> : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label>transcribe_name (sets the AGI&rsquo;s stream client)</Label>
                <select className="tl-input" value={draft.transcribeName} onChange={e => set({ transcribeName: e.target.value })}>
                  {(s.transcribeNames || []).map(t => <option key={t} value={t}>{t}</option>)}
                  {draft.transcribeName && !(s.transcribeNames || []).includes(draft.transcribeName) && <option value={draft.transcribeName}>{draft.transcribeName}</option>}
                </select>
              </div>
              <div>
                <Label>default_language</Label>
                <select className="tl-input" value={draft.defaultLanguage} onChange={e => set({ defaultLanguage: e.target.value })}>
                  {(s.languages || []).map(l => <option key={l} value={l}>{l}</option>)}
                  {draft.defaultLanguage && !(s.languages || []).includes(draft.defaultLanguage) && <option value={draft.defaultLanguage}>{draft.defaultLanguage}</option>}
                </select>
              </div>
              <div>
                <Label>dialing_code</Label>
                <input className="tl-input" value={draft.dialingCode} onChange={e => set({ dialingCode: e.target.value })} />
              </div>
            </div>
            <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{s.languageNote}</div>
            <div className="text-[12.5px]"><strong>Call events webhook</strong>: {webhookSummary(s.values?.webhook_config)}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
              <label className="flex items-center gap-2 text-[12.5px]">
                <input type="checkbox" checked={draft.webhookEnabled} onChange={e => set({ webhookEnabled: e.target.checked })} /> Send call events
              </label>
              <div>
                <Label>URL (blank = switch-wide receiver)</Label>
                <input className="tl-input" value={draft.webhookUrl} onChange={e => set({ webhookUrl: e.target.value })} placeholder="https://…" />
              </div>
              <div>
                <Label>New auth token (blank keeps the current one)</Label>
                <input className="tl-input" type="password" autoComplete="off" value={draft.webhookAuthToken}
                       onChange={e => set({ webhookAuthToken: e.target.value })} />
              </div>
            </div>
            {preview && (
              <div className="space-y-1">
                <div className="text-[12.5px] text-gray-900 dark:text-white">Will change: {preview.changedColumns.join(', ')}</div>
                {preview.changedColumns.map(c => (
                  <div key={c} className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                    {c}: {c === 'webhook_config' ? webhookSummary(preview.before.webhook_config) : String((preview.before as any)[c] ?? '∅')}
                    {' → '}
                    {c === 'webhook_config' ? webhookSummary(preview.after.webhook_config) : String((preview.after as any)[c] ?? '∅')}
                  </div>
                ))}
                {preview.warnings.map(w => <Note key={w} tone="amber">{w}</Note>)}
                {preview.mode !== 'APPLY' && <Note tone="amber">Switch push is {preview.mode.toLowerCase()}: apply is disabled.</Note>}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={busy || isEmptyChange(change)} onClick={doPreview}>Preview</button>
              <button className="tl-btn tl-btn-primary tl-btn-sm" disabled={busy || !preview || preview.mode !== 'APPLY'} onClick={doApply}>Apply</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── The drawer ────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'push' | 'release' | 'retire' | 'settings';

export type DrawerTab = Tab;

export const NumberDetailsDrawer: React.FC<{
  numberId: number; orgs: Organization[]; onClose: (changed: boolean) => void; initialTab?: Tab;
}> = ({ numberId, orgs, onClose, initialTab }) => {
  const [d, setD] = useState<NumberDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(initialTab || 'overview');
  const [allot, setAllot] = useState(false);
  const [changed, setChanged] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await numberLifecycleService.details(numberId);
    if (r.success && r.data) setD(r.data); else setError(r.error || 'Could not load the number');
  }, [numberId]);
  useEffect(() => { load(); }, [load]);
  const touched = () => { setChanged(true); load(); };

  const rollback = async (pushId: number) => {
    setBusy(true); setError(null);
    const r = await numberLifecycleService.rollback(pushId);
    setBusy(false);
    if (!r.success) setError(r.error || 'Rollback was refused');
    else if (r.data && r.data.status !== 'OK') setError(r.data.detail || 'Rollback failed');
    touched();
  };

  const n = d?.number;
  const retired = n?.status === 'RETIRED';
  const TabBtn: React.FC<{ id: Tab; children: React.ReactNode }> = ({ id, children }) => (
    <button className={`tl-btn tl-btn-sm ${tab === id ? 'tl-btn-primary' : 'tl-btn-secondary'}`} onClick={() => setTab(id)}>{children}</button>
  );

  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: 'rgba(0,0,0,.35)' }} onClick={() => onClose(changed)}>
      <div className="h-full w-full max-w-2xl overflow-y-auto p-4 space-y-3" style={{ background: 'var(--surface, #fff)' }}
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{n?.e164 || 'Number'}</h2>
            <div className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
              {d?.provider ? `${d.provider.name}` : ''}{n ? ` · ${n.status}` : ''}{d ? ` · switch push ${d.mode.toLowerCase()}` : ''}
            </div>
          </div>
          <button className="tl-icon-btn" onClick={() => onClose(changed)} aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        {error && <Note tone="danger">{error}</Note>}
        {!d ? <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (
          <>
            <div className="flex gap-1.5 flex-wrap">
              <TabBtn id="overview"><Info className="w-3.5 h-3.5" /> Overview</TabBtn>
              <button className="tl-btn tl-btn-sm tl-btn-secondary" disabled={retired} onClick={() => setAllot(true)}>
                <ArrowRightLeft className="w-3.5 h-3.5" /> {d.holder ? 'Reassign' : 'Allot'}
              </button>
              <TabBtn id="push"><Workflow className="w-3.5 h-3.5" /> Push / Change flow</TabBtn>
              {d.holder && <TabBtn id="release"><Undo2 className="w-3.5 h-3.5" /> Release</TabBtn>}
              {!retired && <TabBtn id="retire"><Archive className="w-3.5 h-3.5" /> Retire</TabBtn>}
              <TabBtn id="settings"><Settings2 className="w-3.5 h-3.5" /> Settings</TabBtn>
              {d.rollbackable && (
                <button className="tl-btn tl-btn-sm tl-btn-secondary" disabled={busy} onClick={() => rollback(d.rollbackable as number)}>
                  <Undo2 className="w-3.5 h-3.5" /> Roll back push #{d.rollbackable}
                </button>
              )}
            </div>

            {tab === 'overview' && (
              <div className="space-y-3">
                <section>
                  <div className="text-[12px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Holder</div>
                  <div className="text-[12.5px] text-gray-900 dark:text-white">
                    {d.holder ? `${d.holder.name || shortId(d.holder.organizationId)} (${d.holder.businessId || 'no business_id'}) since ${formatWhen(d.holder.since)}` : 'In the pool (no holder)'}
                  </div>
                  {(d.tenures || []).map(t => (
                    <div key={t.id} className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                      {t.organizationName || shortId(t.organizationId)} · {formatWhen(t.acquiredAt)} → {t.releasedAt ? formatWhen(t.releasedAt) : 'now'}
                      {t.releasedEarly ? ' · early' : ''}{t.notes ? ` · “${t.notes}”` : ''}
                    </div>
                  ))}
                </section>
                <section>
                  <div className="text-[12px] font-medium mb-1 flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                    <Server className="w-3.5 h-3.5" /> Switch rows (live)
                  </div>
                  {d.switches.map(sw => (
                    <div key={sw.switch} className="text-[12.5px]">
                      <span className="font-medium text-gray-900 dark:text-white">{sw.switch}</span>{' '}
                      <span className={sw.home ? 'tl-pill tl-pill-success' : 'tl-pill'}>{sw.home ? 'home' : 'other'}</span>{' '}
                      {sw.error ? <span style={{ color: 'var(--danger-text)' }}>{sw.error}</span>
                        : (sw.rows || []).length === 0 ? <span style={{ color: 'var(--text-muted)' }}>no row{sw.home ? ' — inbound calls are not routed here' : ''}</span>
                          : (sw.rows || []).map(r => (
                            <span key={r.id} style={{ color: 'var(--text-muted)' }}>
                              {r.cliNumber} · {describeRow(r)} · operator {r.operatorId || '∅'}{r.webhookConfig ? ' · webhook set' : ''}{' '}
                            </span>
                          ))}
                      {sw.note && <div className="text-[12px]" style={{ color: 'var(--amber-label)' }}>{sw.note}</div>}
                    </div>
                  ))}
                  {d.routingRow && (
                    <div className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
                      cpass routing row: {d.routingRow.cliNumber} · {d.routingRow.businessId} · {d.routingRow.operatorId}/{d.routingRow.serviceId} · {d.routingRow.active ? 'active' : 'inactive'}
                    </div>
                  )}
                </section>
                <section>
                  <div className="text-[12px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Billing claim of the current label</div>
                  {d.billing.label ? (
                    <div className="text-[12.5px]">
                      <strong>{d.billing.label}</strong>{' '}
                      {(d.billing.claimedBy || []).length === 0
                        ? <span className="tl-pill tl-pill-coral">claimed by nobody — calls are unbilled</span>
                        : d.billing.holderChainClaims ? <span className="tl-pill tl-pill-success">billed to the holder&rsquo;s chain</span>
                          : <span className="tl-pill tl-pill-amber">billed outside the holder&rsquo;s chain</span>}
                      {(d.billing.claimedBy || []).map(c => (
                        <div key={`${c.organizationId}-${c.pattern}`} className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                          {c.organizationName} · {c.dataSource} · pattern &lsquo;{c.pattern}&rsquo;
                        </div>
                      ))}
                    </div>
                  ) : <div className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>No single home row to read a label from.</div>}
                </section>
                <section>
                  <div className="text-[12px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Recent pushes</div>
                  {(d.pushes || []).length === 0 ? <div className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>None from cpass.</div>
                    : d.pushes.map(p => (
                      <div key={p.id} className="text-[12px] flex items-center gap-1.5 flex-wrap">
                        <span className={actionPill(p.action)}>{ACTION_TEXT[p.action] || p.action}</span>
                        <span className={p.status === 'OK' ? 'tl-pill tl-pill-success' : p.status === 'FAILED' ? 'tl-pill tl-pill-coral' : 'tl-pill'}>{p.status}</span>
                        <span style={{ color: 'var(--text-muted)' }}>
                          #{p.id} · {p.oldBusinessId || '∅'}/{p.oldOperatorId ? `${p.oldOperatorId}/` : ''}{p.oldServiceId || '∅'} → {p.newBusinessId || '∅'}/{p.newOperatorId ? `${p.newOperatorId}/` : ''}{p.newServiceId || '∅'}
                          {' '}· {formatWhen(p.createdAt)} by {p.requestedBy}{p.detail ? ` · ${p.detail}` : ''}
                        </span>
                        {canRollback(p, d.pushes) && (
                          <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={busy} onClick={() => rollback(p.id)}>
                            <Undo2 className="w-3 h-3" /> Rollback
                          </button>
                        )}
                      </div>
                    ))}
                </section>
              </div>
            )}
            {tab === 'push' && <PushPanel details={d} onDone={touched} />}
            {tab === 'release' && <ReleasePanel details={d} onDone={touched} />}
            {tab === 'retire' && <ReleasePanel details={d} retire onDone={touched} />}
            {tab === 'settings' && <SettingsTab numberId={numberId} />}
          </>
        )}
        {allot && n && (
          <AllotModal number={n} orgs={orgs} onClose={c => { setAllot(false); if (c) touched(); }} />
        )}
      </div>
    </div>
  );
};
